import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import type { ProjectListItem, ProjectFilter, WorksClassification, ProjectCreateInput, ProjectStatus } from "../../lib/api";
import { sites as sitesResource } from "../../lib/resources";
import { RecordPicker } from "../../components/RecordPicker";
import type { Site } from "../../lib/resources";
import {
  WORKS_TYPE_LABELS,
  WORKS_TYPE_OPTIONS,
  PROJECT_STATUS_LABELS,
  previewHrb,
  previewSection20,
  gbp,
} from "./projectsUi";
import { useSortableColumn, sortRows, type SortAccessors } from "../../lib/useSortableColumn";
import { SortableHeader } from "../../components/SortableHeader";
import { PostcodeLookup } from "../../components/PostcodeLookup";
import { useAuth } from "../../lib/auth";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { OverflowMenu, overflowItemCls, overflowDangerItemCls } from "../../components/OverflowMenu";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { useOptionsLoader } from "../../lib/useOptionsLoader";
import { useCustomerFilter, CustomerFilterChip } from "./customerFilter";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { PageHeader } from "../../components/PageHeader";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { EmptyState } from "../../components/EmptyState";
import { StatusPill } from "../../components/StatusPill";

// Column sort accessors (Session AU, Item 3). Classification/Status sort by their plain-English
// label; Value numerically. The Compliance badge column is not sortable. Client-side (unpaginated).
const PROJECT_SORT: SortAccessors<ProjectListItem> = {
  ref: (p) => p.projectRef,
  title: (p) => p.title,
  classification: (p) => (p.worksClassification ? WORKS_TYPE_LABELS[p.worksClassification] : null),
  customer: (p) => p.customerName,
  value: (p) => p.contractValue,
  status: (p) => PROJECT_STATUS_LABELS[p.status],
};

// Predict whether the server will ARCHIVE (retained) vs HARD-DELETE from the row's status, mirroring
// ProjectDetailPage's willArchive so the list dialog frames the action honestly (amber/reversible vs
// red/permanent). The server is authoritative: it also weighs milestone invoices, a sent/signed
// quotation and sent/signed subcontractor agreements, which the list row does not carry, so a
// status-only prediction can under-call archive. The response is handled either way (an archived
// project simply leaves the active list).
const RETAINED_STATUSES: ProjectStatus[] = ["accepted", "works_scheduled", "in_progress", "on_hold", "complete", "invoiced"];

const FILTERS: { value: ProjectFilter; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "all", label: "All" },
  { value: "complete", label: "Complete" },
  { value: "lost", label: "Lost" },
  { value: "archived", label: "Archived" },
];

const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green disabled:opacity-50";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";

export function ProjectsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Session ADI: when linked from the customer detail page with ?customerId=, show only that
  // customer's projects. Default the STATUS filter to "all" then (a customer's projects are often
  // complete/invoiced, which the default "active" view hides), so the customer link shows the full set.
  const { customerId, clear } = useCustomerFilter();
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [filter, setFilter] = useState<ProjectFilter>(customerId ? "all" : "active");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showNew, setShowNew] = useState(false);
  // Per-row delete (admin only). The server hard-deletes an early-stage project and archives a
  // retained one (DELETE /api/projects/:id), so the one control covers both; the confirm dialog
  // frames which. On success the row leaves the current (non-archived) view without a full reload.
  const [deletingProject, setDeletingProject] = useState<ProjectListItem | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Card list below md, table otherwise -- decided once at mount (Session AGZ's mechanism).
  // jsdom has no matchMedia, so every existing test still renders the table it pins.
  const [mobileCards] = useState(initialMobileLayout);

  const load = useCallback(async (f: ProjectFilter) => {
    setLoading(true);
    setError(false);
    try {
      setProjects(await api.projects.list(f));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(filter);
  }, [load, filter]);

  const sort = useSortableColumn("projects");
  const sorted = useMemo(() => sortRows(projects, PROJECT_SORT, sort), [projects, sort.sortKey, sort.sortDir]);
  // Apply the customer forward-filter over the sorted rows (client-side; the list is unpaginated).
  const shown = customerId ? sorted.filter((p) => p.customerId === customerId) : sorted;

  const deleteWillArchive = deletingProject ? RETAINED_STATUSES.includes(deletingProject.status) : false;

  async function confirmDelete() {
    if (!deletingProject) return;
    const id = deletingProject.id;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.projects.remove(id);
      // Archived -> hidden from every non-archived filter; hard-deleted -> gone. Either way the row
      // leaves the current view. Remove it locally so there is no full-page reload.
      setProjects((cur) => cur.filter((p) => p.id !== id));
      setDeletingProject(null);
    } catch (e) {
      // The server's message (e.g. a genuine guard) surfaces in the dialog; never fail silently.
      setDeleteError(e instanceof ApiError ? e.message || e.code : "Could not delete the project. Please try again.");
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <div>
      <PageHeader
        className="mb-4"
        title="Projects"
        subtitle="Work beyond routine maintenance and call-outs: modernisations, new installations, repairs, condition reports and post-LOLER remedial works. Each has a scope, a client quotation, subcontractors and a completion sign-off."
        actions={
          <Button onClick={() => setShowNew(true)}>
            <Plus aria-hidden size={15} />
            New project
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`rounded-ele border px-4 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
              filter === f.value ? "border-ele-green bg-ele-inset text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-green"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {customerId && (
        <div className="mb-4">
          <CustomerFilterChip noun="projects" onClear={clear} />
        </div>
      )}

      {loading ? (
        <Loader />
      ) : error ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the projects. Please try again in a moment.
        </p>
      ) : shown.length === 0 ? (
        <EmptyState message={customerId ? "No projects for this customer." : `No ${filter === "all" ? "" : filter} projects.`} />
      ) : mobileCards ? (
        /* Below md the desktop table keeps only Ref, Customer/site and Status -- and a fixed-layout
           table still divides that narrow strip between them, so Ref can clip. The card stacks
           instead: Ref never truncates, and Project title, Classification, Value and the compliance
           badges (all DESKTOP-ONLY columns before this) come back in the details block. */
        <MobileCardList label="Projects">
          {shown.map((p) => (
            <MobileListCard
              key={p.id}
              to={`/office/projects/${p.id}`}
              primary={p.projectRef}
              secondary={p.customerName ?? "-"}
              meta={p.siteName}
              right={
                <StatusPill domain="project" status={p.status} />
              }
              details={
                <div className="space-y-1.5">
                  <p className="text-sm text-ele-strong">{p.title}</p>
                  <p className="text-xs text-ele-body">
                    {p.worksClassification ? WORKS_TYPE_LABELS[p.worksClassification] : "Not set"} · {gbp(p.contractValue)}
                  </p>
                  {p.bsrRequired || p.section20Required ? (
                    <div className="flex flex-wrap gap-1 pt-0.5">
                      {p.bsrRequired ? (
                        <StatusPill
                          word={p.bsrCleared ? "BSR ok" : "BSR!"}
                          tone={p.bsrCleared ? "green" : "red"}
                          title={p.bsrCleared ? "BSR Gateway 2 recorded" : "BSR Gateway 2 approval outstanding"}
                        />
                      ) : null}
                      {p.section20Required ? (
                        <StatusPill word="S20" tone="amber" title="Section 20 leaseholder consultation applies" />
                      ) : null}
                    </div>
                  ) : null}
                </div>
              }
              // Admin-only, and only while the project is not already archived -- matching the
              // desktop table's own gate exactly (reactivation lives on the detail page).
              actions={
                isAdmin && p.archivedAt == null ? (
                  <OverflowMenu label={`Actions for ${p.projectRef}`}>
                    <button
                      role="menuitem"
                      onClick={() => {
                        setDeleteError(null);
                        setDeletingProject(p);
                      }}
                      className={RETAINED_STATUSES.includes(p.status) ? overflowItemCls : overflowDangerItemCls}
                    >
                      {RETAINED_STATUSES.includes(p.status) ? "Archive project" : "Delete project"}
                    </button>
                  </OverflowMenu>
                ) : null
              }
            />
          ))}
        </MobileCardList>
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            <colgroup>
              {/* Admin gains a narrow trailing actions column; the base widths shrink slightly to keep
                  the fixed-layout table at 100%. The Status column is 13 percent, not 8, and the trailing actions column 8 percent with
                  tight padding: a status pill or a menu button in a cell-actions cell is overflow: visible, so at 768 to
                  1000 wide (a phone in landscape, a foldable) a 58px column let the pill spill onto the actions
                  button beside it, and the button spill past the table (Session BDY). Below md only Ref, Customer/site and Status stay
                  visible (Session ACW Loop 3); the row already navigates to the detail page, where
                  the title, classification, value, compliance badges and admin actions all live. */}
              {(isAdmin
                ? ["9%", "21%", "12%", "18%", "10%", "9%", "13%", "8%"]
                : ["10%", "24%", "13%", "19%", "11%", "10%", "13%"]
              ).map((w, i) => {
                const hiddenBelowMd = i === 1 || i === 2 || i === 4 || i === 5 || (isAdmin && i === 7);
                return <col key={i} className={hiddenBelowMd ? "max-md:hidden" : undefined} style={{ width: w }} />;
              })}
              <col className="w-8 md:hidden" />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
              <tr>
                <SortableHeader label="Ref" colKey="ref" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Project" colKey="title" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Classification" colKey="classification" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Customer / site" colKey="customer" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Value" colKey="value" sort={sort} onToggle={sort.toggle} align="right" className="max-md:hidden" />
                <th className="cell-actions max-md:hidden px-3 py-2">Compliance</th>
                <SortableHeader label="Status" colKey="status" sort={sort} onToggle={sort.toggle} className="cell-actions" />
                {isAdmin ? (
                  <th className="cell-actions max-md:hidden px-1.5 py-2 text-right">
                    <span className="sr-only">Actions</span>
                  </th>
                ) : null}
                <th className="w-8 cell-actions md:hidden" aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {shown.map((p) => (
                <tr key={p.id} className="group cursor-pointer border-t border-ele-purple-accent hover:bg-ele-row-hover" onClick={() => navigate(`/office/projects/${p.id}`)}>
                  <td className="px-3 py-2 align-top font-heading text-ele-strong">{p.projectRef}</td>
                  <td className="max-md:hidden px-3 py-2 align-top text-ele-strong">{p.title}</td>
                  <td className="max-md:hidden px-3 py-2 align-top text-ele-body">{p.worksClassification ? WORKS_TYPE_LABELS[p.worksClassification] : "Not set"}</td>
                  {/* cell-wrap (not cell-actions): a long customer/site name WRAPS within the column
                      and grows the row height instead of overrunning sideways into the Value column
                      (Session ACX Fix 3). cell-wrap beats the table's default white-space:nowrap by
                      specificity; break-words handles an unbroken long token. */}
                  <td className="cell-wrap break-words px-3 py-2 align-top text-ele-body">
                    {p.customerName ?? "-"}
                    {p.siteName ? <span className="block text-xs text-ele-body opacity-70">{p.siteName}</span> : null}
                  </td>
                  <td className="max-md:hidden px-3 py-2 text-right align-top text-ele-body">{gbp(p.contractValue)}</td>
                  <td className="cell-actions max-md:hidden px-3 py-2 align-top">
                    {p.bsrRequired ? (
                      <StatusPill
                        word={p.bsrCleared ? "BSR ok" : "BSR!"}
                        tone={p.bsrCleared ? "green" : "red"}
                        className="mr-1"
                        title={p.bsrCleared ? "BSR Gateway 2 recorded" : "BSR Gateway 2 approval outstanding"}
                      />
                    ) : null}
                    {p.section20Required ? (
                      <StatusPill word="S20" tone="amber" title="Section 20 leaseholder consultation applies" />
                    ) : null}
                    {!p.bsrRequired && !p.section20Required ? <span className="text-xs text-ele-body opacity-60">-</span> : null}
                  </td>
                  <td className="cell-actions px-3 py-2 align-top">
                    <StatusPill domain="project" status={p.status} />
                  </td>
                  {isAdmin ? (
                    // Quiet per-row overflow (Session ABA, Loop C1). The menu stops click propagation,
                    // so opening it never triggers the row's navigate. Archived rows carry no delete
                    // (reactivation lives on the detail page); a retained status frames as Archive.
                    <td className="cell-actions max-md:hidden px-1.5 py-2 text-right align-top">
                      {p.archivedAt == null ? (
                        <OverflowMenu label={`Actions for ${p.projectRef}`}>
                          <button
                            role="menuitem"
                            onClick={() => { setDeleteError(null); setDeletingProject(p); }}
                            className={RETAINED_STATUSES.includes(p.status) ? overflowItemCls : overflowDangerItemCls}
                          >
                            {RETAINED_STATUSES.includes(p.status) ? "Archive project" : "Delete project"}
                          </button>
                        </OverflowMenu>
                      ) : null}
                    </td>
                  ) : null}
                  <td className="cell-actions md:hidden pr-1 text-right">
                    <svg aria-hidden viewBox="0 0 16 16" className="ml-auto h-4 w-4 fill-current text-ele-purple transition-colors group-hover:text-ele-green group-focus-within:text-ele-green">
                      <path d="M6 4l4 4-4 4z" />
                    </svg>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNew ? <NewProjectModal onClose={() => setShowNew(false)} onCreated={(id) => navigate(`/office/projects/${id}`)} /> : null}

      {deletingProject ? (
        <ConfirmDeleteModal
          open
          variant={deleteWillArchive ? "archive" : "destructive"}
          title={deleteWillArchive ? "Archive this project?" : "Delete this project?"}
          message={
            deleteWillArchive
              ? `${deletingProject.projectRef} is past the enquiry stage (or has invoices, an agreement or a quotation on file). It will be archived and hidden from the active list, and can be reactivated.`
              : `${deletingProject.projectRef} looks like an early-stage enquiry with no invoices, agreement or quotation, so it will be permanently deleted along with its draft documents. If any of those are on file, it is archived instead and can be reactivated.`
          }
          confirmLabel={deleteWillArchive ? "Archive project" : "Delete project"}
          busy={deleteBusy}
          error={deleteError}
          onConfirm={confirmDelete}
          onCancel={() => { if (!deleteBusy) { setDeletingProject(null); setDeleteError(null); } }}
        />
      ) : null}
    </div>
  );
}

export function NewProjectModal({
  onClose,
  onCreated,
  // When set (e.g. from the dashboard "Raise repair quote" / "Raise modernisation quote" quick
  // actions), the work-type selector opens on this classification. The office can still change it.
  presetClassification,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
  presetClassification?: WorksClassification;
}) {
  // Session AEH, FIX-2 B3: the customer picker loads via useOptionsLoader so a failed fetch shows an
  // inline "Could not load. Retry" beside the field instead of a silently empty dropdown.
  // Session SC2A: the customer is a typeahead picker; its name is kept from the pick for the derived title.
  const [customerName, setCustomerName] = useState("");
  // Session ABB, Loop 2: only what is truly needed to proceed is visible by default (title, work
  // type, client, site, lifts). Everything optional lives behind one collapsed disclosure; its
  // values persist while collapsed and every field can equally be filled in later on the Details
  // tab. Nothing was removed, only tucked away.
  const [showMore, setShowMore] = useState(false);
  const [form, setForm] = useState({
    title: "",
    worksClassification: (presetClassification ?? "modernisation") as WorksClassification,
    customerId: "",
    siteId: "",
    clientName: "",
    clientEmail: "",
    clientPhone: "",
    siteName: "",
    siteAddress: "",
    sitePostcode: "",
    liftDetails: "",
    scopeSummary: "",
    exclusions: "",
    contractValue: "",
    buildingHeightM: "",
    buildingStoreys: "",
    residentialUnits: "",
    leaseholderCharge: "",
    startDate: "",
    targetCompletion: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Session AAB: a known customer's SITES are pickable at create, so a project for an existing
  // site never needs the post-create re-link on the Details tab. Picking a site clears the
  // free-text site fields (the server takes the linked record's details). Session AEH, FIX-2 B3:
  // the sites picker loads via useOptionsLoader so a failed fetch shows an inline Retry rather than
  // an empty dropdown; with no customer picked the loader resolves to an empty list (no fetch).
  const sitesLoader = useOptionsLoader<Site[]>(
    () => (form.customerId ? sitesResource.list({ customerId: form.customerId }) : Promise.resolve<Site[]>([])),
    [form.customerId],
  );
  const customerSites = sitesLoader.data ?? [];

  function set<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  // Live compliance previews (the server is authoritative on create).
  const isHrb = useMemo(() => previewHrb(form), [form]);
  const isS20 = useMemo(() => previewSection20(form.leaseholderCharge), [form.leaseholderCharge]);

  // Session ABB, Loop 4: a blank title derives from the site (else customer, else client) plus
  // the work type -- "Fairfield Lodge: Modernisation" -- shown live in the placeholder, so the
  // title keystrokes are optional. A typed title always wins.
  const derivedTitle = useMemo(() => {
    const base =
      (form.siteId ? customerSites.find((s) => s.id === form.siteId)?.name : form.siteName.trim()) ||
      (form.customerId ? customerName : "") ||
      form.clientName.trim();
    return base ? `${base}: ${WORKS_TYPE_LABELS[form.worksClassification]}` : "";
  }, [form.siteId, form.siteName, form.customerId, form.clientName, form.worksClassification, customerSites, customerName]);

  async function submit() {
    const title = form.title.trim() || derivedTitle;
    if (!title) {
      setErr("Enter a project title, or a site or client name to name it from.");
      return;
    }
    if (!form.customerId && !form.clientName.trim() && !form.siteName.trim()) {
      setErr("Choose an existing customer, or enter a client or site name.");
      return;
    }
    setSaving(true);
    setErr(null);
    const num = (s: string): number | undefined => {
      const x = parseFloat(s);
      return s.trim() !== "" && Number.isFinite(x) ? x : undefined;
    };
    const body: ProjectCreateInput = {
      title,
      worksClassification: form.worksClassification,
      customerId: form.customerId || undefined,
      siteId: form.siteId || undefined,
      clientName: form.clientName || undefined,
      clientEmail: form.clientEmail || undefined,
      clientPhone: form.clientPhone || undefined,
      // A linked site carries its own name/address; the free-text fields apply only when no
      // site record is picked (the server ignores them when siteId is set).
      siteName: form.siteId ? undefined : form.siteName || undefined,
      siteAddress: form.siteId ? undefined : form.siteAddress || undefined,
      sitePostcode: form.siteId ? undefined : form.sitePostcode || undefined,
      liftDetails: form.liftDetails || undefined,
      scopeSummary: form.scopeSummary || undefined,
      exclusions: form.exclusions || undefined,
      contractValue: num(form.contractValue),
      buildingHeightM: num(form.buildingHeightM),
      buildingStoreys: num(form.buildingStoreys),
      residentialUnits: num(form.residentialUnits),
      leaseholderCharge: num(form.leaseholderCharge),
      startDate: form.startDate || undefined,
      targetCompletion: form.targetCompletion || undefined,
      notes: form.notes || undefined,
    };
    try {
      const created = await api.projects.create(body);
      onCreated(created.id);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message || e.code : "Could not create the project.");
      setSaving(false);
    }
  }

  return (
    <ModalShell ariaLabel="New project" onClose={() => !saving && onClose()} width="max-w-[560px]" margin="my-8" panelClassName="p-6">
        <h2 className="mb-3 font-heading text-2xl tracking-wide">New project</h2>

        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className={labelCls}>Project title <span className="text-[10px] normal-case opacity-70">(optional: left blank, it is named from the site and work type)</span></label>
            <input
              className={inputCls}
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder={derivedTitle || "e.g. Bakers Court - two hydraulic lift controller replacements"}
            />
          </div>
          <div>
            {/* Plain English (Session AAB): the stored field is still worksClassification. */}
            <label className={labelCls}>What type of work is this?</label>
            <select aria-label="What type of work is this?" className={inputCls} value={form.worksClassification} onChange={(e) => set("worksClassification", e.target.value as WorksClassification)}>
              {WORKS_TYPE_OPTIONS.map((c) => <option key={c} value={c}>{WORKS_TYPE_LABELS[c]}</option>)}
            </select>
          </div>
        </div>

        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <RecordPicker
              kind="customer"
              label="Existing customer (optional)"
              value={form.customerId}
              placeholder="New / not yet a customer (search to link one)"
              onChange={(id, rec) => {
                setCustomerName(rec?.label ?? "");
                setForm((f) => ({ ...f, customerId: id, siteId: "" }));
              }}
            />
          </div>
          {form.customerId ? (
            <div>
              <label className={labelCls}>Site</label>
              <select aria-label="Site" className={inputCls} value={form.siteId} onChange={(e) => set("siteId", e.target.value)}>
                <option value="">New site (enter details below)</option>
                {customerSites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              {sitesLoader.error ? (
                <p className="mt-1 text-xs text-ele-danger">
                  Could not load sites.{" "}
                  <button type="button" onClick={sitesLoader.retry} className="underline hover:text-ele-strong">Retry</button>
                </p>
              ) : null}
            </div>
          ) : null}
          {!form.customerId ? (
            <>
              <div><label className={labelCls}>Client name</label><input className={inputCls} value={form.clientName} onChange={(e) => set("clientName", e.target.value)} /></div>
              <div><label className={labelCls}>Client email</label><input className={inputCls} value={form.clientEmail} onChange={(e) => set("clientEmail", e.target.value)} /></div>
              <div><label className={labelCls}>Client phone</label><input className={inputCls} value={form.clientPhone} onChange={(e) => set("clientPhone", e.target.value)} /></div>
            </>
          ) : null}
          {!form.siteId ? (
            <>
              <div><label className={labelCls} htmlFor="np-sname">Site name</label><input id="np-sname" className={inputCls} value={form.siteName} onChange={(e) => set("siteName", e.target.value)} /></div>
              <div>
                <label className={labelCls} htmlFor="np-spost">Postcode</label>
                {/* Shared postcode lookup, wired exactly as the ProjectDetailPage client-and-site card
                    (typing a UK postcode fills the site address). Manual entry always works, and with
                    no lookup key configured it is a plain postcode input. */}
                <PostcodeLookup
                  id="np-spost"
                  postcode={form.sitePostcode}
                  onPostcodeChange={(v) => set("sitePostcode", v)}
                  onSelect={(r) => setForm((f) => ({ ...f, sitePostcode: r.postcode || f.sitePostcode, siteAddress: r.address }))}
                  className={inputCls}
                />
              </div>
              <div className="sm:col-span-2"><label className={labelCls} htmlFor="np-saddr">Site address</label><input id="np-saddr" className={inputCls} value={form.siteAddress} onChange={(e) => set("siteAddress", e.target.value)} /></div>
            </>
          ) : null}
        </div>

        <div className="mb-4">
          <label className={labelCls}>Lifts in scope</label>
          <textarea className={inputCls} rows={2} value={form.liftDetails} onChange={(e) => set("liftDetails", e.target.value)} placeholder="e.g. 2 x hydraulic passenger lifts, 6 stops, 1990s controllers" />
        </div>

        {/* Everything optional behind one quiet disclosure (Session ABB, Loop 2). Values entered
            here persist while collapsed and are all equally editable later on the Details tab. */}
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setShowMore((o) => !o)}
            aria-expanded={showMore}
            className="rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong"
          >
            {showMore ? "Hide extra details" : "More details (optional)"}
          </button>
          {!showMore ? (
            <span className="ml-2 text-xs text-ele-body opacity-70">Estimated value, dates, building details, notes. All can be added later on the Details tab.</span>
          ) : null}
        </div>

        {showMore ? (
          <>
            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div><label className={labelCls}>Scope summary</label><textarea className={inputCls} rows={2} value={form.scopeSummary} onChange={(e) => set("scopeSummary", e.target.value)} /></div>
              <div><label className={labelCls}>Exclusions</label><textarea className={inputCls} rows={2} value={form.exclusions} onChange={(e) => set("exclusions", e.target.value)} /></div>
            </div>

            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {/* An enquiry-stage estimate only: once costs are entered, the value is CALCULATED on the
                  Costing tab (cost + markup) and replaces this figure (Session AAA Item 3). */}
              <div><label className={labelCls}>Estimated value (ex VAT)</label><input type="number" className={inputCls} value={form.contractValue} onChange={(e) => set("contractValue", e.target.value)} /></div>
              <div><label className={labelCls}>Planned start</label><input type="date" className={inputCls} value={form.startDate} onChange={(e) => set("startDate", e.target.value)} /></div>
              <div><label className={labelCls}>Target completion</label><input type="date" className={inputCls} value={form.targetCompletion} onChange={(e) => set("targetCompletion", e.target.value)} /></div>
            </div>

            {/* The building questions that trigger the Higher-Risk Building (BSR) and Section 20
                checks. Plain-English labels (Session AAB): these record the CLIENT's statutory
                obligations, mirroring the detail page's advisory card. */}
            <fieldset className="mb-4 rounded-ele border border-ele-purple-accent p-3">
              <legend className="px-1 font-menu text-xs uppercase tracking-wide text-ele-blue">Building details (advisory)</legend>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
                <div><label className={labelCls}>Height (m)</label><input type="number" aria-label="Height (m)" className={inputCls} value={form.buildingHeightM} onChange={(e) => set("buildingHeightM", e.target.value)} /></div>
                <div><label className={labelCls}>Storeys</label><input type="number" aria-label="Storeys" className={inputCls} value={form.buildingStoreys} onChange={(e) => set("buildingStoreys", e.target.value)} /></div>
                <div><label className={labelCls}>Residential units</label><input type="number" aria-label="Residential units" className={inputCls} value={form.residentialUnits} onChange={(e) => set("residentialUnits", e.target.value)} /></div>
                <div>
                  <label className={labelCls}>Highest charge to any one leaseholder <span className="text-[10px] normal-case opacity-70">(Section 20 applies over 250 pounds)</span></label>
                  <input type="number" aria-label="Highest charge to any one leaseholder" className={inputCls} value={form.leaseholderCharge} onChange={(e) => set("leaseholderCharge", e.target.value)} />
                </div>
              </div>
            </fieldset>

            <div className="mb-4">
              <label className={labelCls}>Notes</label>
              <textarea className={inputCls} rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
            </div>
          </>
        ) : null}

        {/* The live compliance previews render OUTSIDE the disclosure, so collapsing it can never
            hide an active statutory warning. */}
        {isHrb ? (
          <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">
            Higher-Risk Building. This project cannot start works until a BSR Gateway 2 approval date is recorded.
          </p>
        ) : null}
        {isS20 ? (
          <p className="mb-3 rounded-ele border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-300">
            Section 20 leaseholder consultation applies (any one leaseholder charged more than 250 pounds). Three consultation stages, minimum 30 days each.
          </p>
        ) : null}

        {err ? <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">{err}</p> : null}

        <div className={`gap-2 ${MODAL_ACTIONS}`}>
          <button onClick={onClose} className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}>Cancel</button>
          <Button onClick={submit} disabled={saving} className={MODAL_ACTION_BTN}>
            {saving ? "Creating..." : "Create project"}
          </Button>
        </div>
    </ModalShell>
  );
}
